import { GraduationCap } from '@phosphor-icons/react';
import { WEEKDAY_SHORT, weekdayOf, type Timetable } from '@shh/shared';
import { Icon, Text, cn } from '@shh/ui';
import { locale } from '../lib/locale';
import { calendarText } from '../i18n/calendar';

/** A child's school week at a glance – today highlighted. */
export function TimetableView({ timetable, today }: { timetable: Timetable; today: string }) {
  const todayIdx = weekdayOf(today);
  const days = (['1', '2', '3', '4', '5'] as const).filter((d) => timetable.days[d]);
  const rows = Math.max(0, ...days.map((d) => timetable.days[d]!.lessons.length));
  if (!days.length) return null;
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-narrow border-separate border-spacing-1 text-sm">
        <caption className="sr-only">{`${timetable.label} – Wochenplan`}</caption>
        <thead>
          <tr>
            {days.map((d) => (
              <th
                key={d}
                scope="col"
                className={cn(
                  'rounded-control px-inset-xs py-inset-xs text-left',
                  Number(d) === todayIdx
                    ? 'bg-action-primary text-text-on-action'
                    : 'bg-surface-sunken',
                )}
              >
                <span className="block font-display text-md font-black">
                  {WEEKDAY_SHORT[locale][Number(d) - 1]}
                </span>
                <span className="block text-xs font-semibold tabular-nums opacity-80">
                  {timetable.days[d]!.start}–{timetable.days[d]!.end}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }, (_, r) => (
            <tr key={r}>
              {days.map((d) => {
                const lesson = timetable.days[d]!.lessons[r];
                return (
                  <td
                    key={d}
                    className={cn(
                      'rounded-control px-inset-xs py-1 align-top',
                      lesson ? 'bg-surface-raised' : '',
                      Number(d) === todayIdx && lesson && 'bg-accent-secondary-soft font-semibold',
                    )}
                  >
                    {lesson ?? ''}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      {rows === 0 && (
        <Text size="sm" tone="muted" className="flex items-center gap-gap-xs pt-gap-sm">
          <Icon icon={GraduationCap} size="sm" /> {calendarText.onlyTimes}
        </Text>
      )}
    </div>
  );
}
